import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useQueryState from "../../../hooks/useQueryState";
import useCountries from "../../../hooks/useCountries";
import { SyntheticEvent, useEffect, useMemo } from "react";
import { Countries } from "../../../types/countries";
import { fixEncoding } from "../../../utils/theme/string";
import { TIER_1_COUNTRY, TIER_2_COUNTRY } from "../../../enums/country";

const SelectCountries = ({ label = "Target country" }: { label?: string }) => {
  const {
    queryState: { country, aggregationLevel },
    setQueryState,
  } = useQueryState();
  const {
    getCountries: { data: countries, isLoading },
  } = useCountries({ country, aggregationLevel });

  const handleChangeCountry = (
    event: SyntheticEvent<Element, Event>,
    value: Countries | null,
  ) => {
    setQueryState({ country: value?.id, geographicalUnit: "" });
  };

  useEffect(() => {
    if (countries && !country) {
      setQueryState({ country: "SOM", geographicalUnit: "" }); // TODO: default SOM
    }
  }, [countries, setQueryState, country]);

  const uniqueCountries = useMemo(() => {
    const map = new Map<string, Countries>();

    countries?.data
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .forEach((c) => {
        if (!c?.id) return;
        map.set(c.id, c);
      });

    return Array.from(map.values());
  }, [countries?.data]);

  const groupTierCountry = (value: string) => {
    if (TIER_1_COUNTRY.includes(value))
      return { label: 1, border: "#43A047", background: "#E8F5E9" };
    if (TIER_2_COUNTRY.includes(value))
      return { label: 2, border: "#FB8C00", background: "#FFF3E0" };
    return { label: 3, border: "#8E24AA", background: "#F3E5F5" };
  };

  return (
    <Box>
      <FormControl fullWidth>
        <Autocomplete<Countries>
          sx={{
            "& .MuiOutlinedInput-root": {
              paddingRight: "16px !important",
            },
          }}
          clearIcon={null}
          filterSelectedOptions
          options={uniqueCountries}
          loading={isLoading}
          getOptionLabel={(option) =>
            `${fixEncoding(option.name)} (${option.id})`
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={label}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {country && (
                      <Chip
                        size="small"
                        label={groupTierCountry(country).label}
                        sx={{
                          mr: 3,
                          borderRadius: "4px",
                          border: `1px solid ${groupTierCountry(country).border}`,
                          background: groupTierCountry(country).background,
                        }}
                      />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Typography>
                  {fixEncoding(option.name)} ({option.id})
                </Typography>
                <Chip
                  size="small"
                  label={`Tier ${groupTierCountry(option.id).label}`}
                  sx={{
                    mr: 3,
                    borderRadius: "4px",
                    border: `1px solid ${groupTierCountry(option.id).border}`,
                    background: groupTierCountry(option.id).background,
                  }}
                />
              </Stack>
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={
            uniqueCountries.find((each) => {
              return each.id === country;
            }) ?? null
          }
          onChange={handleChangeCountry}
          // renderInput={(params) => (
          //   <TextField {...params} label={label} placeholder={label} />
          // )}
        />
      </FormControl>
    </Box>
  );
};

export default SelectCountries;
